import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User } from "lucide-react";
import DOMPurify from "dompurify";
import { fetchBlogPost } from "@/lib/api";

const sanitize = (html) =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "em", "a", "code", "br"], ALLOWED_ATTR: ["href", "class", "target", "rel"] });

function renderContent(content) {
  if (!content) return null;
  const blocks = content.split("\n\n");
  return blocks.map((block, i) => {
    // If a block starts with a heading and has additional lines after it,
    // render the heading + treat the remainder as its own block.
    const headingMatch = block.match(/^(#{2,3})\s+(.+?)(?:\n([\s\S]+))?$/);
    if (headingMatch) {
      const [, hashes, headingText, rest] = headingMatch;
      const HeadingTag = hashes === "##" ? "h2" : "h3";
      const headingClass =
        hashes === "##"
          ? "font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mt-10 mb-4"
          : "font-display font-bold text-xl tracking-tight text-ink mt-8 mb-3";
      return (
        <div key={i}>
          <HeadingTag className={headingClass}>{headingText}</HeadingTag>
          {rest && renderInlineBlock(rest, `${i}-rest`)}
        </div>
      );
    }
    return renderInlineBlock(block, i);
  });
}

function renderInlineBlock(block, key) {
  if (block.match(/^(- |\d+\. )/m)) {
    const lines = block.split("\n");
    const ordered = lines[0].match(/^\d+\. /);
    const items = lines.map((l) => l.replace(/^(- |\d+\.\s+)/, ""));
    const Tag = ordered ? "ol" : "ul";
    return (
      <Tag key={key} className={`my-5 space-y-2 text-ink leading-relaxed pl-6 ${ordered ? "list-decimal" : "list-disc"}`}>
        {items.map((it, k) => (
          <li key={k} dangerouslySetInnerHTML={{ __html: sanitize(inlineMd(it)) }} />
        ))}
      </Tag>
    );
  }
  return (
    <p key={key} className="text-lg text-ink leading-relaxed my-5" dangerouslySetInnerHTML={{ __html: sanitize(inlineMd(block)) }} />
  );
}

function inlineMd(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-forest">$1</strong>')
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then(setPost)
      .catch((e) => setError(e?.response?.status === 404 ? "Post not found." : e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-10">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-forest link-underline">
          <ArrowLeft className="w-4 h-4" /> All essays
        </Link>
      </div>
      {loading && <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-ink-muted" data-testid="post-loading">Loading…</div>}
      {error && <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-terracotta" data-testid="post-error">{error}</div>}
      {post && (
        <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10 lg:py-16">
          <p className="overline text-terracotta">{post.category}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-ink mt-4">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-ink-muted">
            <span className="inline-flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {post.read_minutes} min read</span>
            <span>{post.published_at}</span>
          </div>
          {post.cover_image && (
            <div className="mt-10 rounded-sm overflow-hidden border border-paper-200">
              <img src={post.cover_image} alt={post.title} className="w-full h-[340px] sm:h-[420px] object-cover" />
            </div>
          )}
          <div className="mt-8 prose-none">
            {renderContent(post.content)}
          </div>
          <div className="mt-16 p-8 bg-paper-100 border border-paper-200 rounded-sm">
            <p className="font-display font-bold text-xl text-ink">Working through this yourself?</p>
            <p className="mt-2 text-ink-muted">Book a free intro call and we'll build a plan around your exact situation.</p>
            <Link
              to="/contact"
              data-testid="post-cta"
              className="mt-5 inline-flex items-center gap-2 bg-forest text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-forest-700 hover:-translate-y-0.5 transition-all"
            >
              Book your intro call →
            </Link>
          </div>
        </article>
      )}
    </div>
  );
}
